<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import DartLink from "~/components/text/nodes/DartLink.vue";
import PageLink from "~/components/text/plugins/PageLink.vue";
import {
  getSkillOptionsSync,
  getTaskOptions,
  MINOR_ENTITY_OPTION_FNS_SYNC,
} from "~/components/text/plugins/typeahead/links";
import TypeaheadTaskDocSkillItem from "~/components/text/plugins/typeahead/TypeaheadTaskDocSkillItem.vue";
import { ENTITY_KIND_URL } from "~/constants/kind";
import { AtSignIcon, LinkIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import {
  ButtonSize,
  ButtonStyle,
  EntityName,
  FolderSort,
  IconSize,
  PageKind,
  Placement,
  TypeaheadSpecialEntity,
} from "~/shared/enums";
import type {
  Chat,
  ChatExtraItem,
  Doc,
  MultiselectDropdownMenuItem,
  Skill,
  TypeaheadGetOptionsFn,
  TypeaheadOption,
  ValidationFunctionResult,
} from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";
import { getPageDisplayName } from "~/utils/common";
import usePaginatedList from "~/utils/usePaginatedList";
import { isValidUrlIncludingLocal } from "~/utils/validation";
import { when } from "~/utils/wait";

const TYPEAHEAD_ENTITY_TO_ENTITY_NAME_MAP = new Map([
  [TypeaheadSpecialEntity.TASK, EntityName.TASK],
  [TypeaheadSpecialEntity.DOC, EntityName.DOC],
  [TypeaheadSpecialEntity.DARTBOARD, EntityName.DARTBOARD],
  [TypeaheadSpecialEntity.VIEW, EntityName.VIEW],
  [TypeaheadSpecialEntity.FOLDER, EntityName.FOLDER],
]);

const PAGE_KIND_TO_ENTITY_NAME_MAP = new Map([
  [PageKind.DOC, EntityName.DOC],
  [PageKind.DARTBOARD, EntityName.DARTBOARD],
  [PageKind.VIEW, EntityName.VIEW],
  [PageKind.FOLDER, EntityName.FOLDER],
]);

const MAX_VISIBLE_ENTITIES = 5;

const props = defineProps<{
  chat: Chat;
  extras: ChatExtraItem[];
  isDraft: boolean;
}>();

const emit = defineEmits<{
  insertSkill: [skill: Skill];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();

const menu = ref<InstanceType<typeof MultiselectDropdownMenu> | null>(null);

const existingEntitiesDuidsSet = computed(() => new Set(props.extras.map((e) => e.duid)));

const updateDraftExtras = async (newExtras: ChatExtraItem[], newExtrasAreUserManaged?: boolean) => {
  if (!props.isDraft || !props.chat.draft) {
    return;
  }

  const seenExtras = new Set();
  const uniqueExtras = newExtras.filter((e) => {
    const key = e.kind === ENTITY_KIND_URL ? `${e.url}` : `${e.duid}`;
    if (seenExtras.has(key)) {
      return false;
    }
    seenExtras.add(key);
    return true;
  });

  const draft = { ...props.chat.draft, extras: uniqueExtras };
  await dataStore.updateChat({ duid: props.chat.duid, draft, extrasAreUserManaged: newExtrasAreUserManaged });
};

const onSelectEntity = async (option: TypeaheadOption) => {
  if (typeof option.value !== "string" || !option.specialEntity || !props.chat.draft) {
    return;
  }
  if (option.specialEntity === TypeaheadSpecialEntity.SKILL) {
    const skill = dataStore.getSkillByDuid(option.value);
    if (skill) {
      menu.value?.close();
      await when(computed(() => !menu.value?.isOpen));
      emit("insertSkill", skill);
      return;
    }
  }

  const kind = TYPEAHEAD_ENTITY_TO_ENTITY_NAME_MAP.get(option.specialEntity);
  if (!kind) {
    return;
  }
  updateDraftExtras([...props.chat.draft.extras, { kind, duid: option.value }], true);
};

const removeEntity = (entity: ChatExtraItem) => {
  if (!props.chat.draft) {
    return;
  }

  updateDraftExtras(
    props.chat.draft.extras.filter((e) => e.duid !== entity.duid || e.url !== entity.url),
    true
  );
};

const search = ref("");
const docs = ref<Doc[]>([]);
const docOptions = computed<MultiselectDropdownMenuItem[]>(() =>
  docs.value
    .map((doc) => {
      const folder = dataStore.getFolderByDuid(doc.folderDuid);
      const pageName = getPageDisplayName(folder, dataStore.getSpaceByDuid, "full");
      const spaceAbrev = getPageDisplayName(folder, dataStore.getSpaceByDuid, "abrev");

      const option: TypeaheadOption = {
        value: doc.duid,
        label: doc.title,
        specialEntity: TypeaheadSpecialEntity.DOC,
        adtlSearchTerms: ["doc", pageName, spaceAbrev],
        icon: PageIcon,
        iconArgs: {
          page: doc,
        },
      };

      return {
        value: doc.duid,
        label: doc.title,
        adtlSearchTerms: option.adtlSearchTerms,
        selected: false,
        component: markRaw(TypeaheadTaskDocSkillItem),
        componentArgs: {
          item: option,
          isMultiSelect: true,
          onSelect: (opt: TypeaheadOption) => onSelectEntity(opt),
        },
      };
    })
    .filter((e) => !existingEntitiesDuidsSet.value.has(e.value))
);

const scrollContainer = computed(() => menu.value?.scrollContainer);
const { isLoading: isLoadingDocs, reset: resetDocs } = usePaginatedList(
  computed(() => `recent-docs-${search.value}`),
  scrollContainer,
  computed(() => docs.value.length),
  async (loadedDocsCount) => {
    const { data } = await dataStore.loadDocs({ search: search.value, order: FolderSort.RECENT_DESC }, loadedDocsCount);
    docs.value = Array.from(new Set([...docs.value, ...data.results]));
    return data.count;
  }
);

const tasks = ref<Awaited<ReturnType<TypeaheadGetOptionsFn>> | null>(null);
const { isLoading: isLoadingTasks, reset: resetTasks } = usePaginatedList(
  computed(() => `recent-tasks-${search.value}`),
  scrollContainer,
  computed(() => (tasks.value?.options ?? []).length),
  async () => {
    if (!tasks.value) {
      const response = await getTaskOptions(search.value);
      tasks.value = response;
      return response.total;
    }

    if (!tasks.value.loadMore) {
      return tasks.value.total;
    }

    const response = await tasks.value.loadMore(tasks.value.options.length);
    tasks.value = { ...tasks.value, options: Array.from(new Set([...tasks.value.options, ...response.options])) };
    return response.total;
  }
);

const formatTypeaheadOptions = (options: TypeaheadOption[]): MultiselectDropdownMenuItem[] =>
  options
    .filter((e) => typeof e.value === "string" && !existingEntitiesDuidsSet.value.has(e.value))
    .map((option): MultiselectDropdownMenuItem => {
      const shouldUseTooltip =
        option.specialEntity === TypeaheadSpecialEntity.TASK ||
        option.specialEntity === TypeaheadSpecialEntity.DOC ||
        option.specialEntity === TypeaheadSpecialEntity.SKILL;

      return {
        value: option.value,
        label: option.label,
        adtlSearchTerms: option.adtlSearchTerms,
        selected: false,
        component: markRaw(shouldUseTooltip ? TypeaheadTaskDocSkillItem : DropdownMenuItemContent),
        componentArgs: shouldUseTooltip
          ? {
              item: option,
              isMultiSelect: true,
              onSelect: (opt: TypeaheadOption) => onSelectEntity(opt),
            }
          : {
              title: option.label,
              icon: option.icon ? markRaw(option.icon) : option.icon,
              iconArgs: option.iconArgs,
              isMultiSelect: true,
              onClick: () => onSelectEntity(option),
            },
      };
    });

const contextSections = computed(() => [
  {
    title: "Skills",
    showTitle: true,
    items: formatTypeaheadOptions(getSkillOptionsSync().options),
  },
  {
    title: "Tasks",
    showTitle: true,
    items: formatTypeaheadOptions(tasks.value?.options ?? []),
  },
  {
    title: "Docs",
    showTitle: true,
    items: docOptions.value,
  },
  ...MINOR_ENTITY_OPTION_FNS_SYNC.map((getOptions) => {
    const options = getOptions();
    return {
      title: options.key,
      showTitle: true,
      items: formatTypeaheadOptions(options.options),
    };
  }),
]);

const onSearch = (value: string) => {
  search.value = value;
  resetDocs();
  resetTasks();
};

const syncExtras = async (newExtrasAreUserManaged?: boolean) => {
  if ((newExtrasAreUserManaged ?? props.chat.extrasAreUserManaged) || !props.isDraft) {
    if (props.isDraft && newExtrasAreUserManaged && !props.chat.extrasAreUserManaged) {
      await dataStore.updateChat({
        duid: props.chat.duid,
        extrasAreUserManaged: true,
      });
    }
    return;
  }

  const extras: ChatExtraItem[] = [];
  const { currentPage } = appStore;
  const pageEntityName = currentPage ? PAGE_KIND_TO_ENTITY_NAME_MAP.get(currentPage.pageKind) : undefined;
  if (pageEntityName) {
    extras.push({
      kind: pageEntityName,
      duid: currentPage?.duid,
    });
  }

  if (appStore.selectedTaskDuids.size > 0) {
    extras.push(
      ...[...appStore.selectedTaskDuids].map((duid) => ({
        kind: EntityName.TASK,
        duid,
      }))
    );
  }

  if (appStore.docOpenInFullscreen) {
    extras.push({
      kind: EntityName.DOC,
      duid: appStore.docOpenInFullscreen.duid,
    });
  }

  await updateDraftExtras(extras, newExtrasAreUserManaged);
};

const addUrl = (value: string) => {
  if (!props.chat.draft) {
    return;
  }
  updateDraftExtras([...props.chat.draft.extras, { kind: ENTITY_KIND_URL, url: value }], true);
};

const validate = (value: string): ValidationFunctionResult =>
  isValidUrlIncludingLocal(value) ? { isValid: true } : { isValid: false, error: "Invalid URL" };

const openUrl = (url: string | undefined) => {
  if (!url) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

const visibleEntities = computed(() => {
  if (props.extras.length > MAX_VISIBLE_ENTITIES) {
    return props.extras.slice(0, MAX_VISIBLE_ENTITIES - 1);
  }
  return props.extras;
});
const hiddenEntities = computed(() => {
  if (props.extras.length > MAX_VISIBLE_ENTITIES) {
    return props.extras.slice(MAX_VISIBLE_ENTITIES - 1);
  }
  return [];
});
const hiddenEntitiesCount = computed(() => hiddenEntities.value.length);
const hiddenEntitiesText = computed(() =>
  hiddenEntities.value
    .map((entity) => {
      if (entity.kind === ENTITY_KIND_URL) {
        return entity.url;
      }
      if (!entity.duid) {
        return undefined;
      }
      const entityData = dataStore.getEntity(entity.duid, entity.kind);
      if (!entityData) {
        return undefined;
      }
      return entityData.title;
    })
    .filter(isDefined)
    .map((e) => `• ${e}`)
);

watch(
  () => [props.chat.duid, appStore.currentPage, [...appStore.selectedTaskDuids], appStore.docOpenInFullscreen],
  () => syncExtras()
);

onMounted(() => {
  if (props.isDraft && props.chat.draft?.extras.length === 0) {
    syncExtras();
  }
});

defineExpose({
  syncExtras,
});
</script>

<template>
  <div
    v-if="isDraft || extras.length > 0"
    class="mb-0.5 w-full overflow-hidden px-1 text-vlt"
    :class="!isDraft ? '-mx-1 mt-2' : 'pt-1'">
    <div class="flex min-w-0 flex-wrap items-center gap-1" :class="isDraft ? 'mx-1' : 'mr-1'">
      <MultiselectDropdownMenu
        v-if="isDraft"
        ref="menu"
        placeholder="Search"
        :sections="contextSections"
        header-title="Add context"
        :placement="Placement.BOTTOM_LEFT"
        :distance="2"
        infinite-list
        :loading-more="isLoadingDocs || isLoadingTasks"
        :make-new-entry-text="(text: string) => `Add link ${text}`"
        :validate="validate"
        enable-new-entry
        @search="onSearch"
        @create="addUrl">
        <Tooltip text="Add context">
          <Button
            :text="extras.length === 0 ? 'Add context' : undefined"
            :btn-style="ButtonStyle.SECONDARY"
            :size="ButtonSize.CHIP"
            :icon="AtSignIcon"
            :icon-size="IconSize.XS"
            borderless
            a11y-label="Add context"
            class="!px-0.5 text-xs border-md"
            :class="extras.length === 0 ? '!py-px' : '!py-0.5'"
            text-style="text-lt" />
        </Tooltip>
      </MultiselectDropdownMenu>

      <template v-for="entity in visibleEntities" :key="entity.url ?? entity.duid">
        <PageLink
          v-if="entity.kind === ENTITY_KIND_URL"
          :title="entity.url ?? ''"
          :is-chat-enabled-mode="isDraft"
          :is-chat-disabled-mode="!isDraft"
          :icon="LinkIcon"
          :icon-args="{ class: 'icon-xs' }"
          class="!inline-flex min-w-0 align-middle text-xs"
          @click="openUrl(entity.url)"
          @remove="() => removeEntity(entity)" />

        <DartLink
          v-else
          :is-chat-enabled-mode="isDraft"
          :is-chat-disabled-mode="!isDraft"
          :name="entity.kind"
          :duid="entity.duid"
          class="min-w-0 text-xs"
          @remove="() => removeEntity(entity)" />
      </template>

      <Tooltip v-if="hiddenEntitiesCount > 0" :text="hiddenEntitiesText">
        <div class="inline-flex h-5 cursor-default items-center gap-1.5 rounded px-1 text-xs bg-lt">
          <span class="text-lt">+{{ hiddenEntitiesCount }}</span>
        </div>
      </Tooltip>
    </div>
  </div>
</template>
